<script lang="ts" setup>
/**
 * Read-only, syntax-highlighted viewer for an HTTP body (transaction request/response).
 *
 * Detects the content language — from the Content-Type media type when known, otherwise by
 * sniffing the text — then pretty-prints (JSON/XML) and highlights it with a minimal read-only
 * CodeMirror 6 instance (the project already ships CM6 + lang-json/xml/javascript, see BbelField).
 * Deliberately NOT the BBEL editor: no autocomplete/linting/editing — just a formatted, coloured,
 * scrollable view. Unknown/plain content falls back to a monospace block with no highlighting.
 * With `slots`, `{{ }}` expressions take the builder's slot colours over the base language.
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { EditorState, Prec, type Extension } from '@codemirror/state'
import { EditorView, lineNumbers } from '@codemirror/view'
import { HighlightStyle, syntaxHighlighting, foldGutter, codeFolding } from '@codemirror/language'
import { tags as t } from '@lezer/highlight'
import { json } from '@codemirror/lang-json'
import { xml } from '@codemirror/lang-xml'
import { javascript } from '@codemirror/lang-javascript'
import { bbelSlotHighlight } from './bbel/bbel-language'
import { formatBytes } from '../utils/transactionFormatters'

const props = defineProps<{
  body: string
  /** The response/request Content-Type media type, if the transaction captured one. */
  mediaType?: string | null
  /** The engine's `DataType` name for the content: JSON, XML, HTML, PLAINTEXT, BINARY. */
  declaredType?: string | null
  lengthBytes?: number | null
  downloadUrl?: string | null
  /** Colour `{{ }}` slots over the base language. */
  slots?: boolean
  /** Highlight as this language instead of detecting one. */
  language?: 'javascript' | null
}>()

type BodyLang = 'json' | 'xml' | 'html' | 'javascript' | 'text'

/** Decide the language: an explicit `language` wins, then the media type; otherwise sniff the first non-whitespace chars. */
const detectedLang = computed<BodyLang>(() => {
  if (props.language) return props.language
  const mt = (props.mediaType ?? '').toLowerCase()
  if (mt) {
    if (mt.includes('json') || mt.endsWith('+json')) return 'json'
    if (mt.includes('html')) return 'html'
    if (mt.includes('xml') || mt.endsWith('+xml')) return 'xml'
    if (mt.includes('javascript') || mt.includes('ecmascript')) return 'javascript'
    if (mt.includes('text/plain')) return sniff(props.body) // some APIs send JSON as text/plain
  }
  return sniff(props.body)
})

function sniff(text: string): BodyLang {
  const s = text.trimStart()
  if (!s) return 'text'
  if (s[0] === '{' || s[0] === '[') {
    try {
      JSON.parse(text)
      return 'json'
    } catch {
      /* not valid JSON — fall through */
    }
  }
  if (s.startsWith('<')) {
    // Distinguish HTML from generic XML by the doctype / <html root.
    if (/^<!doctype html/i.test(s) || /^<html[\s>]/i.test(s)) return 'html'
    return 'xml'
  }
  return 'text'
}

/** Pretty-print where we safely can; otherwise show the body verbatim. */
const formatted = computed<string>(() => {
  const lang = detectedLang.value
  if (lang === 'json') {
    try {
      return JSON.stringify(JSON.parse(props.body), null, 2)
    } catch {
      return props.body
    }
  }
  if (lang === 'xml' || lang === 'html') return formatXml(props.body)
  return props.body
})

// A light-touch XML/HTML indenter — inserts newlines between tags and re-indents. Not a full
// parser (self-closing tags, CDATA, comments are left as-is), which is fine for a read-only preview.
function formatXml(src: string): string {
  const withBreaks = src.replace(/>\s*</g, '>\n<').trim()
  let depth = 0
  return withBreaks
    .split('\n')
    .map((raw) => {
      const line = raw.trim()
      if (/^<\/\w/.test(line)) depth = Math.max(depth - 1, 0)
      const indented = '  '.repeat(depth) + line
      // Opening tag that isn't self-closing and doesn't close on the same line → indent children.
      if (/^<\w[^>]*[^/]>$/.test(line) && !/^<[^>]+>.*<\/[^>]+>$/.test(line)) depth++
      return indented
    })
    .join('\n')
}

// --- Highlight style keyed to Bootstrap theme variables (tracks light/dark) ------------------
// Mirrors the base-language palette BbelField uses so JSON/XML colours are consistent across the app.
const bodyHighlight = HighlightStyle.define([
  { tag: [t.propertyName, t.definition(t.propertyName)], color: 'var(--bs-info-text-emphasis, #055160)' },
  { tag: [t.string, t.special(t.string)], color: 'var(--bs-danger-text-emphasis, #58151c)' },
  { tag: [t.number, t.bool, t.null], color: 'var(--bs-success-text-emphasis, #0a3622)' },
  { tag: [t.keyword, t.atom], color: 'var(--bs-warning-text-emphasis, #664d03)' },
  { tag: [t.tagName, t.angleBracket], color: 'var(--bs-info-text-emphasis, #055160)' },
  { tag: [t.attributeName], color: 'var(--bs-pink, #d63384)' },
  { tag: [t.comment], color: 'var(--bs-secondary-color, #6c757d)', fontStyle: 'italic' },
  { tag: [t.punctuation, t.separator], color: 'var(--bs-secondary-color, #6c757d)' },
])

function langExtension(lang: BodyLang): Extension[] {
  switch (lang) {
    case 'json':
      return [json()]
    // HTML rides the XML grammar — good enough for read-only highlighting, and avoids pulling in
    // @codemirror/lang-html just for the occasional HTML response body.
    case 'xml':
    case 'html':
      return [xml()]
    case 'javascript':
      return [javascript()]
    default:
      return []
  }
}

const hostEl = ref<HTMLElement | null>(null)
let view: EditorView | null = null

function buildState(): EditorState {
  return EditorState.create({
    doc: formatted.value,
    extensions: [
      EditorView.editable.of(false),
      EditorState.readOnly.of(true),
      EditorView.lineWrapping,
      lineNumbers(),
      codeFolding(),
      foldGutter(),
      syntaxHighlighting(bodyHighlight),
      ...langExtension(detectedLang.value),
      // Highest precedence nests the slot marks innermost, so a slot inside a JSON string keeps its colour.
      ...(props.slots ? [Prec.highest(bbelSlotHighlight())] : []),
      viewerTheme,
    ],
  })
}

const viewerTheme = EditorView.theme({
  '&': { backgroundColor: 'transparent', color: 'var(--bs-body-color)', fontSize: '0.8125rem' },
  '.cm-content': { fontFamily: 'var(--bs-font-monospace, monospace)' },
  '.cm-gutters': {
    backgroundColor: 'transparent',
    color: 'var(--bs-secondary-color)',
    border: 'none',
  },
  '.cm-activeLine, .cm-activeLineGutter': { backgroundColor: 'transparent' },
  '&.cm-focused': { outline: 'none' },
})

onMounted(() => {
  if (hostEl.value) view = new EditorView({ state: buildState(), parent: hostEl.value })
})

// Rebuild on new content or a new way of highlighting it (e.g. switching between request and response).
watch(
  () => [props.body, props.mediaType, props.language, props.slots],
  () => view?.setState(buildState()),
)

onBeforeUnmount(() => view?.destroy())

const DECLARED_TYPE_LABELS: Record<string, string> = {
  JSON: 'JSON',
  XML: 'XML',
  HTML: 'HTML',
  PLAINTEXT: 'Plain text',
  BINARY: 'Binary',
}

const DETECTED_LANG_LABELS: Record<BodyLang, string> = {
  json: 'JSON',
  xml: 'XML',
  html: 'HTML',
  javascript: 'JavaScript',
  text: 'Plain text',
}

// The engine's declared type is the truth when there is one; a type with no label here shows its own name.
const typeLabel = computed(() => {
  if (!props.declaredType) return DETECTED_LANG_LABELS[detectedLang.value]
  return DECLARED_TYPE_LABELS[props.declaredType] ?? props.declaredType
})

// Copy the body as stored, not as pretty-printed. A refused clipboard (no permission, insecure page) does nothing.
async function copy() {
  try {
    await navigator.clipboard.writeText(props.body)
  } catch {
    // The user sees nothing copied and can select the text instead.
  }
}
</script>

<template>
  <div class="code-body-viewer">
    <div class="d-flex align-items-center gap-3 px-2 pt-1">
      <small class="text-body-secondary">
        {{ typeLabel }}<template v-if="lengthBytes != null"> · {{ formatBytes(lengthBytes) }}</template>
      </small>
      <button
        type="button"
        class="btn btn-link btn-sm p-0 ms-auto text-body-secondary"
        aria-label="Copy"
        title="Copy"
        @click="copy"
      >
        <i class="bi bi-clipboard"></i>
      </button>
      <a
        v-if="downloadUrl"
        class="btn btn-link btn-sm p-0 text-body-secondary"
        :href="downloadUrl"
        target="_blank"
        rel="noopener"
        aria-label="Download"
        title="Download"
      >
        <i class="bi bi-download"></i>
      </a>
    </div>
    <div ref="hostEl" class="code-body-cm"></div>
  </div>
</template>

<style scoped lang="scss">
.code-body-viewer {
  border: 1px solid var(--bs-border-color);
  border-radius: var(--bs-border-radius);
  background: var(--bs-tertiary-bg);
  overflow: hidden;
}

.code-body-cm :deep(.cm-editor) {
  max-height: 360px;
}

.code-body-cm :deep(.cm-scroller) {
  overflow: auto;
}
</style>
